/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ReactNode } from 'react'
import { ModalConfig, Brand } from '@typescript'
import { FullScreenModal, type FullScreenModalProps } from '@ui/Dialog'
import { useSiteContext } from '@contexts/MkSiteContext'
import { Register } from '@account/components/Register'
import { StepName } from '@account/components/Register/types'
import { SignIn } from '@account/components/SignIn'
import type { SignInStepName } from '@account/components/SignIn/types'
import Modal from '@components/ui/Modal/Modal'
import { withBase } from '@utils/basePath'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { DataStore, EMkStorageKeys } from '@shared/utils/DataStore'
import { isLoginPath } from '@auth/constants/authConstants'

/**
 * ModalContextProps - The interface for the modal context value
 *
 * Provides all the methods needed to manage modals throughout the application.
 * This context is consumed by the useModal hook.
 */
export type ModalContextProps = {
  /** Closes all currently open modals */
  closeAllModals?: () => void
  /** Closes a specific modal by ID, or all modals if no ID provided */
  closeModal: (modalID?: string) => () => void
  /** Closes a modal by ID within a specific modal state array */
  closeModalById?: (modalID: string, options: { modalsToQuery: ModalState[] }) => ModalState[]
  /** Opens a new modal with the specified content and configuration */
  openModal: (modalID: string, content?: React.ReactNode, config?: ModalConfig, backdrop?: boolean) => void
  /** Opens a sign in modal */
  openSignInModal: (modalID?: string, error?: string, startStep?: SignInStepName) => void
  /** Opens a register modal */
  openRegisterModal: (modalID?: string, startStep?: StepName) => void
  /** Checks if a modal with the given ID is currently open */
  isModalOpen: (modalID: string) => boolean
}

/**
 * ModalContext - React Context for modal management
 *
 * Provides the modal management functions to all child components.
 * Must be used within a ModalProvider.
 */
export const ModalContext = React.createContext<ModalContextProps>({
  closeAllModals: () => {},
  closeModal: (modalID?: string) => () => {},
  closeModalById: (modalID: string, _options: { modalsToQuery: ModalState[] }) => [],
  openModal: (modalID: string, content?: React.ReactNode, config?: ModalConfig, backdrop?: boolean) => {},
  openSignInModal: (modalID?: string, error?: string, startStep?: SignInStepName) => {},
  openRegisterModal: (modalID?: string, startStep?: StepName) => {},
  isModalOpen: (modalID: string) => false,
})

/** Renders SignIn; title comes from SignIn's step-based labels. Expects `AuthLabelsProvider` from `AppProviders` (ancestor of `ModalProvider`). */
const SignInModalContent: React.FC<{
  titleClassName?: string
  postSignIn?: () => void
  initialError?: string
  startStep?: SignInStepName
}> = ({ titleClassName, postSignIn, initialError, startStep }) => (
  <SignIn
    titleClassName={titleClassName}
    postSignIn={postSignIn}
    initialError={initialError}
    startStep={startStep}
  />
)

/**
 * ModalProviderProps - Props for the ModalProvider component
 */
export interface ModalProviderProps {
  /** Child components to be wrapped by the modal provider */
  children?: ReactNode
  /** Title for the modal */
  title?: string
}

/**
 * ModalState - Represents the state of an individual modal
 *
 * Tracks all the necessary information for rendering and managing a modal,
 * including its content, configuration, and visibility state.
 */
export type ModalState = {
  /** Unique identifier for the modal */
  id: string
  /** React content to be rendered inside the modal */
  content?: React.ReactNode | null
  /** Styling and behavior configuration for the modal */
  config?: ModalConfig
  /** Whether the modal should have a backdrop overlay */
  backdrop?: boolean
  /** Whether the modal is currently visible */
  isOpen: boolean
  /** Whether this is a full-screen modal with background media */
  isFullScreen?: boolean
  /** Props for full-screen modal (background image/video, etc.) */
  fullScreenProps?: Omit<FullScreenModalProps, 'isOpen' | 'onClose'>
  /** Title for the modal */
  title?: string
  /** Whether modal has the default X in the header for closing the modal */
  closable?: boolean
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children, title }) => {
  const [modals, setModals] = React.useState<ModalState[]>([])
  const { themeBrand: brandFromContext } = useSiteContext()
  const authContext = useAuthentication()
  const { isLoggingOut } = authContext

  // Use a ref to track the current isLoggingOut value
  const isLoggingOutRef = React.useRef(isLoggingOut)
  React.useEffect(() => {
    isLoggingOutRef.current = isLoggingOut
  }, [isLoggingOut])

  const getBrandVideoSrc = (brand: string): string | undefined => {
    const videoSrc = Brand.tryVideoSrcFor(brand)
    return videoSrc ? withBase(videoSrc) : undefined
  }

  const closeAllModals = React.useCallback(() => {
    setModals((prevModals) => {
      const updatedModals = prevModals.map((modal) => ({
        ...modal,
        isOpen: false,
        content: null,
      }))
      return updatedModals
    })
  }, [])

  const closeModalById = React.useCallback(
    (modalID: string, { modalsToQuery }: { modalsToQuery: ModalState[] }) =>
      modalsToQuery.map((modal: ModalState) => {
        const currentModal = { ...modal }

        if (currentModal.id === modalID) {
          currentModal.isOpen = false
          currentModal.content = null
        }

        return currentModal
      }),
    [],
  )

  const isModalOpen = React.useCallback((modalID: string): boolean => modals.some((modal) => modal.id === modalID && modal.isOpen), [modals])

  const closeModal = React.useCallback(
    (modalID?: string) => () => {
      // Dispatch modalClose event for SignIn component to handle
      window.dispatchEvent(new CustomEvent('modalClose'))

      if (!modalID || modalID === '') {
        return closeAllModals()
      }

      return setModals((prev) => closeModalById(modalID, { modalsToQuery: prev }))
    },
    [closeAllModals, closeModalById],
  )

  const openModal = React.useCallback((modalID: string, content?: React.ReactNode, config?: ModalConfig, backdrop?: boolean) => {
    const modalUpdate: ModalState = {
      id: modalID,
      content,
      title: config?.title,
      config: config as ModalConfig,
      backdrop: backdrop ?? config?.backdrop,
      isFullScreen: config?.modal,
      fullScreenProps: config as Omit<FullScreenModalProps, 'isOpen' | 'onClose'>,
      isOpen: !!content,
      closable: config?.closable,
    }

    // Scroll to top when opening a fullscreen modal
    if (config?.modal) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    setModals((prevModals) => {
      const updatedModals = [...prevModals]
      const existingModalIndex = updatedModals.findIndex((modal) => modal.id === modalID)

      // If modal already exists and is open, just update it without closing all modals
      if (existingModalIndex !== -1 && updatedModals[existingModalIndex].isOpen) {
        updatedModals[existingModalIndex] = {
          ...updatedModals[existingModalIndex],
          ...modalUpdate,
        }
        return updatedModals
      }

      // If it's a new modal or the existing modal is closed, close all others first.
      // Also clear their content so their subtree (e.g., SignIn/LoginFields) unmounts;
      // otherwise multiple modals would keep duplicate form instances alive and compete
      // over shared context state, causing render loops. Reopen-by-id still works because
      // openModal overwrites `content` in the reopen branch below.
      const closedModals = updatedModals.map((modal) => ({
        ...modal,
        isOpen: false,
        content: null,
      }))

      if (existingModalIndex !== -1) {
        closedModals[existingModalIndex] = {
          ...closedModals[existingModalIndex],
          ...modalUpdate,
        }
        return closedModals
      }

      const newModals = [
        ...closedModals,
        {
          ...modalUpdate,
        },
      ]
      return newModals
    })
  }, [])

  const openSignInModal = React.useCallback(
    (modalID?: string, error?: string, startStep?: SignInStepName) => {
      const currentIsLoggingOut = isLoggingOutRef.current
      const searchParams = new URLSearchParams(window.location.search)
      const redirectTo = searchParams.get('redirectTo')
      const loginOrigin = isLoginPath(window.location.pathname) && redirectTo ? redirectTo : window.location.pathname
      DataStore.setItem(EMkStorageKeys.LOGIN_ORIGIN, loginOrigin)
      // Mirror to localStorage so a full-page OAuth redirect can still recover the origin
      // if the per-tab sessionStorage value is lost during the round trip.
      if (redirectTo) {
        DataStore.setItem(EMkStorageKeys.LOGIN_ORIGIN, redirectTo, { store: localStorage })
      }

      // Allow opening modal during logout if there's an error to display
      // This prevents error messages from being lost during the logout process
      if (currentIsLoggingOut && !error) {
        return
      }

      const modalId = modalID || `${brandFromContext}-login-modal`

      // If modal is already open, update it with new error/step instead of returning early
      if (isModalOpen(modalId)) {
        // Update the existing modal with new error or step
        setModals((prevModals) =>
          prevModals.map((modal) => {
            if (modal.id === modalId) {
              return {
                ...modal,
                content: (
                  <SignInModalContent
                    titleClassName="modal-title-1"
                    postSignIn={() => {
                      setModals((prev) => prev.map((m) => (m.id === modalId ? { ...m, isOpen: false } : m)))
                    }}
                    initialError={error}
                    startStep={startStep}
                  />
                ),
              }
            }
            return modal
          }),
        )
        return
      }

      const handlePostSignIn = () => {
        // Close the modal after successful login
        setModals((prevModals) => prevModals.map((modal) => (modal.id === modalId ? { ...modal, isOpen: false } : modal)))
      }

      // Scroll to top when opening sign-in modal
      window.scrollTo({ top: 0, behavior: 'smooth' })

      const videoSrc = getBrandVideoSrc(brandFromContext)

      openModal(
        modalId,
        <SignInModalContent
          titleClassName="modal-title-1"
          postSignIn={handlePostSignIn}
          initialError={error}
          startStep={startStep}
        />,
        {
          modal: true,
          backdrop: true,
          video: videoSrc ? { src: videoSrc, type: 'video/mp4' } : undefined,
        },
      )
    },
    [brandFromContext, openModal, isModalOpen],
  )

  const openRegisterModal = React.useCallback(
    (modalID?: string, startStep?: StepName) => {
      // Scroll to top when opening register modal
      window.scrollTo({ top: 0, behavior: 'smooth' })

      const videoSrc = getBrandVideoSrc(brandFromContext)

      const modalId = modalID || `${brandFromContext}-register-modal`

      openModal(modalId, <Register startStep={startStep} />, {
        modal: true,
        backdrop: true,
        registerShell: true,
        video: videoSrc ? { src: videoSrc, type: 'video/mp4' } : undefined,
      })
    },
    [brandFromContext, openModal],
  )

  React.useEffect(
    () => () => {
      closeAllModals()
    },
    [],
  )

  const contextValue = React.useMemo(
    () => ({
      closeAllModals,
      closeModal,
      closeModalById,
      openModal,
      openSignInModal,
      openRegisterModal,
      isModalOpen,
    }),
    [closeAllModals, closeModal, closeModalById, openModal, openSignInModal, openRegisterModal, isModalOpen],
  )

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      {modals.map((modal) => {
        if (modal.isFullScreen && modal.fullScreenProps) {
          return (
            <FullScreenModal
              appendTo={document.getElementById('mainView') as HTMLElement}
              key={modal.id}
              isOpen={modal.isOpen}
              modal={false}
              closeButtonPosition="content"
              onClose={() => closeModal(modal.id)()}
              video={((): any => {
                const src = getBrandVideoSrc(brandFromContext)
                return src ? { src, type: 'video/mp4' } : undefined
              })()}>
              {modal?.content}
            </FullScreenModal>
          )
        }

        return (
          <Modal
            id={modal.id}
            title={modal.title}
            closable={modal.closable}
            visible={modal.isOpen}
            backdrop={modal.backdrop}
            config={modal.config}
            onClose={() => closeModal(modal.id)()}>
            {modal.content}
          </Modal>
        )
      })}
    </ModalContext.Provider>
  )
}

/**
 * useModal Hook
 *
 * A custom React hook that provides convenient access to modal management functions.
 * Must be used within a ModalProvider component.
 *
 * @param options - Configuration options for the hook
 * @param options.modalID - Default modal ID for this hook instance (optional)
 * @param options.config - Default configuration for modals opened by this hook (optional)
 *
 * @returns Object containing modal management functions
 *
 * @throws {Error} When used outside of a ModalProvider
 *
 * @example
 * ```tsx
 * const { openModal, closeModal } = useModal({
 *   modalID: 'my-modal',
 *   config: { header: { className: 'custom-header' } }
 * })
 *
 * // Open a modal
 * openModal(<div>Content</div>)
 *
 * // Close the modal
 * closeModal()
 * ```
 */
export const useModal = (options: { modalID?: string; config?: ModalConfig } = {}) => {
  const { modalID: modalIdx = 'none', config: globalConfig } = options
  const context = React.useContext(ModalContext)
  if (!context) {
    throw new Error(`[${modalIdx}] useModal must be used within a ModalProvider`)
  }

  /**
   * Opens a modal with the specified content and configuration
   *
   * @param content - React content to render in the modal
   * @param modalID - Modal ID (uses default if not provided)
   * @param config - Modal configuration (merged with global config)
   * @param backdrop - Whether to show backdrop (default: true)
   */
  const openModal = (content?: React.ReactNode, modalID?: string, config?: ModalConfig, backdrop: boolean = true) => {
    context.openModal(modalID || modalIdx, content, { ...globalConfig, ...config } as ModalConfig, backdrop)
  }

  /**
   * Closes a modal by ID, or the default modal if no ID provided
   *
   * @param modalID - Modal ID to close (optional)
   * @returns Function that can be called to close the modal
   */
  const closeModal = (modalID?: string) => () => {
    context.closeModal(modalID || modalIdx)()
  }

  return {
    ...context,
    openModal,
    closeModal,
  }
}
